/**
 * Client-only auth state (00-contracts §14.1: auth, theme and active organization are the three
 * React contexts; everything server-shaped belongs to TanStack Query).
 *
 * The provider never gates rendering on the session: guards do that in `beforeLoad`, where a
 * redirect is a redirect rather than a flash of the wrong layout.
 */
import type { Session } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { endSession } from './session';
import { supabase } from './supabase';

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

export interface AuthContextValue {
  status: AuthStatus;
  session: Session | null;
  email: string | null;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  signUpWithPassword: (
    email: string,
    password: string,
    /** `invitationToken` er det `app.before_user_created` krever — se implementasjonen. */
    metadata: { givenName: string; familyName: string; invitationToken?: string },
  ) => Promise<{ hasSession: boolean }>;
  sendPasswordReset: (email: string, redirectTo: string) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * supabase-js reports failures as a returned `error`, not a throw. Turning it into a throw once,
 * here, is what lets every form use the same react-query error path.
 *
 * ---------------------------------------------------------------------------------------------
 * MED EN KODE, OG IKKE BARE EN SETNING (QA12 #2).
 *
 * Atle skrev feil passord og fikk «Handlingen ble ikke fullført. Prøv igjen — hvis det gjentar
 * seg, oppgi forespørsels-ID-en til support.» Det er meldingen for en uventet systemfeil, og en
 * feilskrevet passord er ikke det: det er det vanligste som skjer på den siden.
 *
 * GoTrue svarer med en maskinlesbar `code` («invalid_credentials», «email_not_confirmed»,
 * «over_request_rate_limit»). Den bæres videre her, og `errorKey` gjør den til en setning på
 * norsk. Kjenner vi ikke koden, faller vi tilbake til den generiske — som før.
 */
export class SupabaseAuthError extends Error {
  readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = 'SupabaseAuthError';
    this.code = code;
  }
}

function raise(error: { message: string; code?: string; status?: number } | null): void {
  if (!error) return;
  /*
   * Koden finnes ikke på hver eneste feil GoTrue sender — eldre svar har bare teksten. Da leses
   * den av meldingen, og bare for de to tilfellene som betyr noe på en innloggingsside.
   */
  const fallback = /invalid login credentials/i.test(error.message)
    ? 'invalid_credentials'
    : /email not confirmed/i.test(error.message)
      ? 'email_not_confirmed'
      : 'unknown';
  throw new SupabaseAuthError(error.code ?? fallback, error.message);
}

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');

  useEffect(() => {
    let active = true;

    void supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session);
      setStatus(data.session ? 'authenticated' : 'anonymous');
    });

    const { data: subscription } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      setStatus(nextSession ? 'authenticated' : 'anonymous');
    });

    return () => {
      active = false;
      subscription.subscription.unsubscribe();
    };
  }, []);

  const signInWithPassword = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    raise(error);
  }, []);

  const signUpWithPassword = useCallback(
    async (
      email: string,
      password: string,
      metadata: { givenName: string; familyName: string; invitationToken?: string },
    ) => {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          /*
           * Never read for authorization — user_metadata is writable by the user (§13.1). It
           * exists so GoTrue can pass a display name to the provisioning call.
           *
           * OG SIDEN 20. SEPTEMBER BÆRER DEN INVITASJONSTOKENET (K-12, sak 169).
           *
           * Det er ikke en motsigelse av setningen over. Tokenet AUTORISERER ingenting her: det
           * er en hemmelighet `app.before_user_created` slår opp i `invitations` for å avgjøre om
           * kontoen får bli til i det hele tatt. At brukeren kan skrive feltet selv betyr bare at
           * hen kan skrive et token som ikke finnes — og da nektes kontoen.
           *
           * Uten dette feltet nekter hooken ENHVER kontoopprettelse, også den inviterte sin.
           */
          data: {
            given_name: metadata.givenName,
            family_name: metadata.familyName,
            ...(metadata.invitationToken === undefined
              ? {}
              : { invitation_token: metadata.invitationToken }),
          },
        },
      });
      raise(error);
      return { hasSession: data.session !== null };
    },
    [],
  );

  const sendPasswordReset = useCallback(async (email: string, redirectTo: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
    raise(error);
  }, []);

  const updatePassword = useCallback(async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    raise(error);
  }, []);

  const signOut = useCallback(async () => {
    await endSession(queryClient);
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      session,
      email: session?.user.email ?? null,
      signInWithPassword,
      signUpWithPassword,
      sendPasswordReset,
      updatePassword,
      signOut,
    }),
    [
      status,
      session,
      signInWithPassword,
      signUpWithPassword,
      sendPasswordReset,
      updatePassword,
      signOut,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside <AuthProvider>.');
  return value;
}
